import { IPatient } from '@alberta/konexi-shared';
import { Inject, Injectable } from '@angular/core';
import { Deferred } from '@common/deferred/deferred';
import { AccountService } from '@services/account.service';
import { IFeathersAppProvider } from '@services/contracts/sync/feathers-app-provider';
import { FeathersService } from '@services/feathers.service';
import { Observable, of } from 'rxjs';
import { AttachmentDto } from '../../models/attachment/attachment-dto.model';
import { AttachmentCacheService } from './attachment-cache.service';
import { IAttachmentService } from './attachment-service.interface';
import { OfflineUploadResult, UploadResult } from './attachment-transfer.service';
import { AttachmentUtilsService } from './attachment-utils.service';
import { CreateMetadata } from './attachment.service';
import { WebAttachmentTransferService } from './web-attachment-transfer.service';

/**
 * Web implementation of {@link IAttachmentService}.
 * Only injected on non-native platforms (see `AttachmentServiceToken` provider in `app.module.ts`);
 * native uses `AttachmentService`.
 *
 * Storage model: nothing is synced or permanently stored locally. *Metadata* is always fetched
 * live from the server (feathers websocket). *Binaries* are fetched from the server on demand
 * and kept in an evictable IndexedDB LRU cache (`AttachmentCacheService`, max 200MB / 80% quota)
 * — so previously viewed attachments survive short offline phases, everything else requires a
 * connection.
 */
@Injectable({ providedIn: 'root' })
export class WebAttachmentService implements IAttachmentService {
  private _feathersAttachmentService: any;
  private _ready = new Deferred();

  constructor(
    private readonly _attachmentTransferService: WebAttachmentTransferService,
    @Inject(FeathersService) private _feathersAppProvider: IFeathersAppProvider,
    private readonly _attachmentUtils: AttachmentUtilsService,
    private _accountService: AccountService,
    private readonly _attachmentCacheService: AttachmentCacheService // Inject the cache service
  ) {}
  isAttachmentBlobLocallyAvailable(id: string): Promise<boolean> {
    // Attachments are not synced in web and thus are not locally available
    return Promise.resolve(false);
  }
  getLocalAttachments(): Promise<string[]> {
    throw new Error('Method not implemented: getLocalAttachments.');
  }
  get offlineTxSuccess(): Observable<OfflineUploadResult> {
    // used by attach-list.components.ts to
    // update attachment status while upload is happening
    return of();
  }

  /**
   * Returns the binary, cache-first:
   * 1. IndexedDB LRU cache hit → returned without network (works offline).
   * 2. Miss → fetched from the server and written to the cache (cache write failures are
   *    logged but non-fatal).
   *
   * Rejects when the blob is neither cached nor fetchable (offline, or missing on the server).
   */
  async download(id: string, mimeType: string): Promise<Blob> {
    const userId = await this._accountService.getCurrentUserId();

    // Check if the attachment is cached
    try {
      const cachedBlob = await this._attachmentCacheService.getCachedAttachment(userId, id);
      if (cachedBlob) {
        return cachedBlob;
      }
    } catch (error) {
      window.logger.error(`Failed to get cached attachment ${id}, falling back to download`, error);
    }

    // Fetch from server if not cached
    const blob = await this._attachmentTransferService.fetchBlob(id, mimeType);

    // Cache the downloaded blob
    try {
      await this._attachmentCacheService.cacheAttachment(userId, id, blob);
    } catch (cacheError) {
      window.logger.error(`Failed to cache attachment ${id}`, cacheError);
    }

    return blob;
  }
  upload(localId: string): Promise<UploadResult | null> {
    throw new Error('Method not implemented: upload.');
  }

  createAttachmentForState(
    patient: IPatient,
    createResult: UploadResult,
    meta: CreateMetadata
  ): Promise<Partial<AttachmentDto & { displayName: string }>> {
    throw new Error('Method not implemented: createAttachmentForState.');
  }

  runAttachmentQueuesProcessor(): Promise<void> {
    // called by web-sync-starter.ts
    // NOOP in WEB
    return Promise.resolve();
  }
  async init(): Promise<void> {
    await this._feathersAppProvider.ready;
    this._feathersAttachmentService = this._feathersAppProvider.app.service('attachment');
    this._ready.resolve();
    return Promise.resolve();
  }
  /** No-op on web — there is no local metadata store to save into. */
  save(attachments: AttachmentDto[]): Promise<void> {
    return;
  }
  /**
   * Returns the binary of an attachment. Always hits the server for the *metadata* first
   * (content type, via {@link getAttachmentDto}), then resolves the *binary* via
   * {@link download} (cache-first).
   *
   * Failure modes:
   * - Metadata missing on server or offline: {@link getAttachmentDto} throws
   *   (`Attachment result empty for <id>`) — even when the blob itself would be in the cache.
   * - Blob missing on server (and not cached): {@link download} rejects.
   *
   * @param returnValue Desired representation: `'blob'`, `'base64'` or `'blobUrl'`.
   */
  async get<T extends 'blobUrl' | 'blob' | 'base64'>(
    id: string,
    returnValue: T
  ): Promise<T extends 'blob' ? Blob : string> {
    await this._ready.promise;
    const metadata = await this.getAttachmentDto(id);
    const blob = await this.download(id, metadata.contentType);
    return this._attachmentUtils.transformBlob(blob, returnValue) as Promise<T extends 'blob' ? Blob : string>;
  }

  async create(base64String: string, metadata: CreateMetadata): Promise<UploadResult> {
    await this._ready.promise;
    const dataForUpload = await this._attachmentUtils.prepareAttachmentData(base64String, metadata);
    const uploadResult = await this._attachmentTransferService.upload({
      blob: dataForUpload.blob,
      metadata: dataForUpload.uploadMetadata,
      filename: dataForUpload.filename,
      mime: dataForUpload.mime,
    });

    const userId = await this._accountService.getCurrentUserId();

    try {
      await this._attachmentCacheService.cacheAttachment(userId, uploadResult.data.id, dataForUpload.blob);
    } catch (cacheError) {
      window.logger.error(`Failed to cache attachment ${uploadResult.data.id}`, cacheError);
    }

    return uploadResult;
  }
  update(attachment: AttachmentDto): Promise<boolean> {
    return this._attachmentTransferService.update(attachment._id, attachment);
  }
  delete(id: string): Promise<void> {
    return this._attachmentTransferService.delete(id);
  }

  public async softDelete(attachment: AttachmentDto): Promise<boolean> {
    attachment.metadata.archived = true;
    return this._attachmentTransferService.update(attachment._id, attachment);
  }

  /**
   * Fetches the attachment *metadata* live from the server (`attachment.find({_id: {$in: [id]}})`).
   * There is no local fallback on web.
   *
   * @throws `Attachment result empty for <id>` when the attachment does not exist on the server
   *         *or* the request failed (offline/server error — the find error is logged and the
   *         empty-result path is taken either way).
   */
  public async getAttachmentDto(id: string): Promise<AttachmentDto> {
    await this._ready.promise;
    let result: any;
    try {
      result = await this._feathersAttachmentService.find({
        query: {
          _id: { $in: [id] },
        },
      });
    } catch (error) {
      window.logger.error(`Failed to load attachment ${id} from server`, error);
    }
    if (result?.data?.length) {
      return result.data[0];
    } else {
      throw new Error(`Attachment result empty for ${id}`);
    }
  }
}
